import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { AppState } from '@/core/state';
import type { Settings, SkillView } from '@/core/types';
import type { Band, TextLevel } from '@/i18n';
import { clock } from '@/core/util/misc';
import { studioStore } from './services';
import { eligibilityOf, viewsOf } from '@/engine/views';

export const store = studioStore();

/** Subscribe to a slice of state. Selectors must return stable references for unchanged data. */
export function useStudio<T>(selector: (s: AppState) => T): T {
  const ref = useRef<{ state: AppState | null; value: T | undefined }>({ state: null, value: undefined });
  const read = () => {
    const s = store.getState();
    if (ref.current.state !== s) {
      ref.current = { state: s, value: selector(s) };
    }
    return ref.current.value as T;
  };
  return useSyncExternalStore(store.subscribe, read, read);
}

export function useAppState(): AppState {
  return useSyncExternalStore(store.subscribe, store.getState, store.getState);
}

/** Current time, refreshed periodically so due dates and decay stay live. */
export function useNow(intervalMs = 60_000): number {
  const [now, setNow] = useState(() => clock.now());
  useEffect(() => {
    const id = setInterval(() => setNow(clock.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function useViews(): Record<string, SkillView> {
  const state = useAppState();
  const now = useNow();
  return viewsOf(state, now);
}

export function useEligibility() {
  const state = useAppState();
  return eligibilityOf(state);
}

/** Whether the operating system asks for reduced motion. */
export function useSystemReducedMotion(): boolean {
  const [sys, setSys] = useState(() => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    if (typeof matchMedia === 'undefined') return;
    const mq = matchMedia('(prefers-reduced-motion: reduce)');
    const fn = () => setSys(mq.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return sys;
}

/**
 * The motion level actually in effect: exactly what the user chose, except that
 * a device-level "reduce motion" request caps it at 'reduced' when the user has
 * asked Studio to respect it.
 */
export function useMotion(): Settings['motion'] {
  const motion = useStudio((s) => s.settings.motion);
  const follow = useStudio((s) => s.settings.motionFollowSystem);
  const sys = useSystemReducedMotion();
  return effectiveMotion(motion, follow, sys);
}

export function effectiveMotion(motion: Settings['motion'], follow: boolean, sysReduced: boolean): Settings['motion'] {
  if (follow && sysReduced && motion === 'full') return 'reduced';
  return motion;
}

export function useReducedMotion(): boolean {
  return useMotion() !== 'full';
}

/** Presentation band: the learner's stage decides unless Settings override it. */
export function bandForStage(stage: string | undefined): Band {
  const m = /^G(\d+)$/.exec(stage ?? '');
  if (m) {
    const g = Number(m[1]);
    if (g <= 4) return 'junior';
    if (g <= 7) return 'middle';
  }
  return 'senior';
}

/** Interface language difficulty in effect: 'auto' is easy for grade 1–4, normal otherwise. */
export function textLevelFor(setting: Settings['textLevel'], stage: string | undefined): TextLevel {
  if (setting !== 'auto') return setting;
  return bandForStage(stage) === 'junior' ? 'easy' : 'normal';
}

export function useTextLevel(): TextLevel {
  const setting = useStudio((s) => s.settings.textLevel);
  const stage = useStudio((s) => s.profile?.education.stage);
  return textLevelFor(setting, stage);
}

export function useBand(): Band {
  const setting = useStudio((s) => s.settings.band);
  const stage = useStudio((s) => s.profile?.education.stage);
  return setting === 'auto' ? bandForStage(stage) : setting;
}

/** Animated count-up for numbers (respects reduced motion). */
export function useCountUp(target: number, duration = 900): number {
  const reduced = useReducedMotion();
  const [v, setV] = useState(target);
  const from = useRef(target);
  useEffect(() => {
    if (reduced) {
      setV(target);
      return;
    }
    const start = performance.now();
    const a = from.current;
    let raf = 0;
    const tick = (t: number) => {
      const k = Math.min(1, (t - start) / duration);
      const e = 1 - Math.pow(1 - k, 3);
      setV(a + (target - a) * e);
      if (k < 1) raf = requestAnimationFrame(tick);
      else from.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, reduced]);
  return v;
}
